/**
 * Cart page quantity stepper (snippets/cart-quantity-stepper.liquid) and the
 * remove button beside it.
 *
 * A visible cart row can own hidden paid lines: Infinite Options / Zepto
 * add-ons on US, Bold option lines on UK. Changing the row alone would leave
 * those behind and mis-charge the add-ons, so every change is written as ONE
 * /cart/update.js call that moves the row and its linked lines together,
 * addressed by line key (line indexes go stale when hidden lines shift,
 * ENG-76).
 *
 * Shopify matches a line key it cannot find by variant id instead, so a write
 * from a page that no longer matches the cart (changed in another tab, say)
 * can land on a different line. Each write is therefore checked against the
 * live cart first, and a stale page reloads instead of writing.
 *
 * Concurrent writes to one cart are last-writer-wins: an app setting a cart
 * attribute at the same moment (several do, on page load) can silently undo
 * a quantity write that returned 200. Each write is read back and resent if
 * it did not hold.
 */
(() => {
  'use strict';

  const COMMIT_DELAY_MS = 500;
  const WRITE_ATTEMPTS = 3;

  // Same glyph as the Add to Cart spinner (stores/us/src/assets/atc-click-feedback.js).
  const SPINNER_PATH =
    'M232,128a104,104,0,0,1-208,0c0-41,23.81-78.36,60.66-95.27a8,8,0,0,1,6.68,14.54C60.15,61.59,40,93.27,40,128a88,88,0,0,0,176,0c0-34.73-20.15-66.41-51.34-80.73a8,8,0,0,1,6.68-14.54C208.19,49.64,232,87,232,128Z';

  const pending = new Map();
  let timer = null;
  let committing = false;
  let afterCommit = null;

  const cartRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Desktop and mobile markup each render a stepper for the same line.
  const steppersFor = (key) =>
    Array.from(document.querySelectorAll('[data-cart-qty]')).filter(
      (stepper) => stepper.dataset.lineKey === key
    );

  const initialQuantity = (stepper) => Number(stepper.dataset.quantity);

  const maxQuantity = (stepper) => {
    const max = Number(stepper.dataset.max);
    return max > 0 ? Math.max(max, initialQuantity(stepper)) : Infinity;
  };

  // "line key|quantity|owner quantity" per hidden line.
  const linkedLines = (stepper) =>
    (stepper.dataset.linkedLines || '')
      .split(',')
      .filter(Boolean)
      .map((entry) => {
        const [key, quantity, ownerQuantity] = entry.split('|');
        return {
          key,
          quantity: Number(quantity),
          ownerQuantity: Number(ownerQuantity) || initialQuantity(stepper),
        };
      });

  // Every line the pending changes touch: `updates` is what to write,
  // `rendered` the quantities this page was built with. Linked lines keep
  // their per-unit ratio to the row (or rows) that own them, and leave with
  // a row that is being removed (quantity 0).
  const plan = () => {
    const updates = {};
    const rendered = {};
    for (const [key, quantity] of pending) {
      const stepper = steppersFor(key)[0];
      const initial = initialQuantity(stepper);
      updates[key] = quantity;
      rendered[key] = initial;
      for (const line of linkedLines(stepper)) {
        const from = line.key in updates ? updates[line.key] : line.quantity;
        const change = Math.round(((quantity - initial) * line.quantity) / line.ownerQuantity);
        updates[line.key] = Math.max(quantity === 0 ? 0 : 1, from + change);
        rendered[line.key] = line.quantity;
      }
    }
    return { updates, rendered };
  };

  const writeInputs = (key, quantity) => {
    for (const input of document.querySelectorAll('[data-cart-qty-input]')) {
      if (input.dataset.cartQtyInput === key) input.value = quantity;
    }
  };

  // The form posts updates[] from these inputs, so linked lines are kept in
  // step in the DOM too, not only in the request.
  const show = (stepper, quantity) => {
    const key = stepper.dataset.lineKey;
    const { updates } = plan();
    writeInputs(key, quantity);
    for (const line of linkedLines(stepper)) {
      writeInputs(line.key, line.key in updates ? updates[line.key] : line.quantity);
    }
    for (const twin of steppersFor(key)) {
      twin.querySelector('[data-cart-qty-step="-1"]').disabled = quantity <= 1;
      twin.querySelector('[data-cart-qty-step="1"]').disabled = quantity >= maxQuantity(twin);
    }
  };

  // While a row is being removed its bin icon gives way to a spinner (both
  // the desktop and the mobile bin; the spinner takes the icon's size classes).
  const showRemoving = (row) => {
    for (const bin of row.querySelectorAll('[data-cart-remove]')) {
      const icon = bin.querySelector('svg');
      const sizing = icon
        ? Array.from(icon.classList).filter((name) => !name.startsWith('feather'))
        : ['size-5'];
      if (icon) icon.style.display = 'none';
      bin.setAttribute('aria-busy', 'true');
      bin.insertAdjacentHTML(
        'beforeend',
        `<svg class="animate-spin ${sizing.join(' ')}" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" data-cart-remove-spinner><path d="${SPINNER_PATH}"></path></svg>`
      );
    }
  };

  const clearRemoving = () => {
    for (const spinner of document.querySelectorAll('[data-cart-remove-spinner]')) {
      const bin = spinner.closest('[data-cart-remove]');
      spinner.remove();
      bin.removeAttribute('aria-busy');
      const icon = bin.querySelector('svg');
      if (icon) icon.style.display = '';
    }
  };

  const setBusy = (busy) => {
    const list = document.querySelector('#cartform .cart-item-list');
    if (!list) return;
    list.setAttribute('aria-busy', String(busy));
    list.style.opacity = busy ? '0.6' : '';
    list.style.pointerEvents = busy ? 'none' : '';
  };

  const revert = () => {
    const keys = Array.from(pending.keys());
    pending.clear();
    for (const key of keys) {
      const stepper = steppersFor(key)[0];
      if (stepper) show(stepper, initialQuantity(stepper));
    }
    afterCommit = null;
    committing = false;
    clearRemoving();
    setBusy(false);
  };

  const liveItems = async () => {
    const response = await fetch(cartRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('cart.js failed');
    return (await response.json()).items;
  };

  // True when the cart holds exactly these quantities for these lines; a line
  // that is not in the cart counts as 0.
  const holds = (items, quantities) => {
    const live = new Map(items.map((item) => [item.key, item.quantity]));
    return Object.entries(quantities).every(([key, quantity]) => (live.get(key) || 0) === quantity);
  };

  const HIDDEN_TYPES = ['OPTIONS_HIDDEN_PRODUCT', 'PPLR_HIDDEN_PRODUCT'];

  // The Infinite Options group the shared cart section gives a row: its own,
  // or, when it has none, that of the hidden option line directly beneath it.
  const orderGroup = (items, owner) => {
    const own = (owner.properties || {})._io_order_group;
    if (own) return own;
    for (const item of items.slice(items.indexOf(owner) + 1)) {
      const group = (item.properties || {})._io_parent_order_group;
      if (item.product_type === 'OPTIONS_HIDDEN_PRODUCT' && group) return group;
      if (!HIDDEN_TYPES.includes(item.product_type)) return null;
    }
    return null;
  };

  // Hidden lines the cart ties to `owner`, by the same rules the cart
  // sections link them with: Bold builder id (UK section); Infinite Options
  // order group and Zepto pricing ref (shared section, `grouped`).
  const linkedInCart = (items, owner, grouped) => {
    const builder = (owner.properties || {})._boldBuilderId;
    const pricingRef = grouped ? (owner.properties || {})._pc_pricing_ref : null;
    const group = grouped ? orderGroup(items, owner) : null;
    return items.filter((item) => {
      const props = item.properties || {};
      if (item.key === owner.key) return false;
      if (builder && props._boldBuilderId === builder && props._boldVariantIds == null) return true;
      if (pricingRef && props._pc_pricing_ref === pricingRef && !props._pc_pricing_qty_split) return true;
      return Boolean(group) && props._io_parent_order_group === group;
    });
  };

  // Stale: a line about to be written has moved, or the cart has an add-on
  // line for it that this page was not rendered with (Bold creates missing
  // option lines after the cart page has loaded).
  const isStale = (items, rendered) =>
    !holds(items, rendered) ||
    Array.from(pending.keys()).some((key) => {
      const owner = items.find((item) => item.key === key);
      // Only the shared section's rows carry data-io-order-group.
      const row = steppersFor(key)[0].closest('.cart-item');
      const grouped = Boolean(row) && 'ioOrderGroup' in row.dataset;
      return linkedInCart(items, owner, grouped).some((line) => !(line.key in rendered));
    });

  const commit = async () => {
    clearTimeout(timer);
    if (committing || pending.size === 0) return;
    committing = true;
    setBusy(true);

    const { updates, rendered } = plan();

    const strings = (window.theme && window.theme.strings) || {};
    const errorMessage =
      strings.cart_update_error || 'Something went wrong updating your cart. Please try again.';

    let response;
    let held = false;
    try {
      if (isStale(await liveItems(), rendered)) {
        window.location.assign(cartRoot() + 'cart');
        return;
      }
      let resend = updates;
      for (let attempt = 0; attempt < WRITE_ATTEMPTS && !held; attempt++) {
        response = await fetch(cartRoot() + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: resend }),
        });
        if (!response.ok) break;

        const items = await liveItems();
        held = holds(items, updates);
        if (held) break;

        // A key that has left the cart must not be sent again (Shopify would
        // apply it to another line of the same variant). Lines being removed
        // that are already gone are dropped; if a line that should stay has
        // gone, another writer changed the cart, so stop and reload.
        const inCart = new Set(items.map((item) => item.key));
        const gone = Object.keys(updates).filter((key) => !inCart.has(key));
        if (gone.some((key) => updates[key] > 0)) break;
        resend = Object.fromEntries(Object.entries(updates).filter(([key]) => inCart.has(key)));
      }
    } catch (err) {
      console.error('Error updating cart quantities', err);
      revert();
      window.alert(errorMessage);
      return;
    }

    if (!response.ok) {
      // Shopify explains stock limits in `description`; show that, then
      // reload so the page matches the cart again.
      const body = await response.json().catch(() => ({}));
      console.error('Cart quantity update rejected', response.status, body);
      window.alert(body.description || errorMessage);
      window.location.assign(cartRoot() + 'cart');
      return;
    }

    if (afterCommit && held) {
      pending.clear();
      committing = false;
      afterCommit();
      return;
    }

    window.location.assign(cartRoot() + 'cart');
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-qty-step]');
    if (!button) return;
    event.preventDefault();
    if (committing) return;

    const stepper = button.closest('[data-cart-qty]');
    const key = stepper.dataset.lineKey;
    const initial = initialQuantity(stepper);
    const current = pending.has(key) ? pending.get(key) : initial;
    const next = Math.min(
      maxQuantity(stepper),
      Math.max(1, current + Number(button.dataset.cartQtyStep))
    );
    if (next === current) return;

    if (next === initial) {
      pending.delete(key);
    } else {
      pending.set(key, next);
    }
    show(stepper, next);

    clearTimeout(timer);
    if (pending.size > 0) timer = setTimeout(commit, COMMIT_DELAY_MS);
  });

  // The bin removes the row and its linked lines in the same checked write,
  // with no confirm() prompt: browsers can suppress those, which leaves a
  // button that does nothing. Without this script the link still removes the
  // row itself.
  document.addEventListener('click', (event) => {
    const bin = event.target.closest('[data-cart-remove]');
    if (!bin) return;
    const row = bin.closest('.cart-item');
    const stepper = row && row.querySelector('[data-cart-qty]');
    if (!stepper) return;

    event.preventDefault();
    if (committing) return;
    pending.set(stepper.dataset.lineKey, 0);
    showRemoving(row);
    commit();
  });

  // A checkout click during a change waits for the write, then goes through.
  document.addEventListener(
    'submit',
    (event) => {
      if (!event.target.matches('#cartform')) return;
      if (!committing && pending.size === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const form = event.target;
      const submitter = event.submitter;
      afterCommit = () => {
        // The cart already holds the new quantities. updates[] is positional
        // and rows may have just left the cart, so the form must not post it.
        for (const input of form.querySelectorAll('[name="updates[]"]')) input.disabled = true;
        form.requestSubmit(submitter);
      };
      commit();
    },
    true
  );

  // The builder (Cuttlefish) re-adds an edited item at quantity 1 and reuses
  // the add-on lines already in the cart. A line above 1 therefore goes back
  // to 1, add-ons included, before the builder opens; otherwise the edited
  // item would come back as one item still carrying several add-ons.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cart-edit-link]');
    if (!link) return;
    const row = link.closest('.cart-item');
    const stepper = row && row.querySelector('[data-cart-qty]');
    if (!stepper) return;

    if (committing) {
      event.preventDefault();
      return;
    }

    const key = stepper.dataset.lineKey;
    if (initialQuantity(stepper) > 1) {
      pending.set(key, 1);
    } else {
      pending.delete(key);
    }
    show(stepper, 1);
    if (pending.size === 0) {
      clearTimeout(timer);
      return;
    }

    event.preventDefault();
    afterCommit = () => window.location.assign(link.href);
    commit();
  });
})();
